"use client";

import { useMemo, useState } from "react";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from "../../../components/ui/dialog";
import { ProjectCard } from "./client-card";
import { Input } from "../../../components/ui/input";
import { Search } from "lucide-react";
import { Button } from "../../../components/ui/button";
import { AddProjectDialog } from "./add-client-project-dialog";
import { deleteProjectIframe } from "../../../API";

import jsPDF from "jspdf";
import { svg2pdf } from "svg2pdf.js";
import { fabric } from "fabric";

// ======================
// DOWNLOAD PDF
// ======================
export async function handleDownload(project: Project) {
    (window as any)['stateLoadinScreen2'](true);
    // ⚠️ ajuste se o svg não for o mesmo nome da thumb
    const svgUrl =
        "https://nfghuenhuaecfyccyaib.supabase.co/storage/v1/object/public/svgs/" +
        project.thumb;

    const thumbUrl =
        "https://nfghuenhuaecfyccyaib.supabase.co/storage/v1/object/public/images/" +
        project.thumb;

    const pdf = new jsPDF({
        orientation: "l", // paisagem
        unit: "mm",
        format: "a4",
        compress: true,
    });

    const pageW = pdf.internal.pageSize.getWidth();
    const pageH = pdf.internal.pageSize.getHeight();

    // Layout
    const m = 8;
    const outerX = m;
    const outerY = m;
    const outerW = pageW - m * 2;
    const outerH = pageH - m * 2;

    const headerH = 22;
    const contentH = 120;
    const gap = 6;
    const commentsH = outerH - headerH - contentH - gap;

    const contentY = outerY + headerH;
    const commentsY = contentY + contentH + gap;

    const leftW = outerW * 0.55;
    const rightW = outerW - leftW - gap;
    const leftX = outerX;
    const rightX = outerX + leftW + gap;

    // ===== TEMPLATE =====
    pdf.setLineWidth(0.6);
    pdf.roundedRect(outerX, outerY, outerW, outerH, 2, 2);

    pdf.setLineWidth(0.25);
    pdf.line(outerX, outerY + headerH, outerX + outerW, outerY + headerH);

    const logoLink =
        "https://nfghuenhuaecfyccyaib.supabase.co/storage/v1/object/public/images/" +
        localStorage.getItem("user_id") +
        "_logo";

    const logoBoxW = 24;
    const logoBoxH = 16;
    const logoX = outerX + 2;
    const logoY = outerY + 2;

    // desenha moldura
    pdf.rect(logoX, logoY, logoBoxW, logoBoxH);

    try {
        const { dataUrl, w, h } = await imageUrlToPngDataUrl(logoLink);

        const fitted = fitContain(logoBoxW - 2, logoBoxH - 2, w, h);

        pdf.addImage(
            dataUrl,
            "PNG",
            logoX + 1 + fitted.x,
            logoY + 1 + fitted.y,
            fitted.w,
            fitted.h,
            undefined,
            "FAST"
        );
    } catch (err) {
        // fallback se não encontrar a logo
        pdf.setFontSize(8);
        pdf.text("Logo", logoX + 6, logoY + 8);
    }


    const today = new Date();
    const dd = String(today.getDate()).padStart(2, '0');
    const mm = String(today.getMonth() + 1).padStart(2, '0'); // January is 0!
    const yyyy = today.getFullYear();

    const formattedToday = `${dd}/${mm}/${yyyy}`;

    // Header texts
    pdf.setFontSize(9);
    pdf.text(`Cliente: ${project.name ?? ""}`, outerX + 30, outerY + 8);
    pdf.text(`Telefone: ${project.phone ?? ""}`, outerX + 30, outerY + 14);
    pdf.text(`Email: ${project.email ?? ""}`, outerX + 95, outerY + 8);
    pdf.text(`Data: ${formattedToday ?? ""}`, outerX + 95, outerY + 14);

    // Boxes
    pdf.rect(leftX, contentY, leftW, contentH);
    pdf.rect(rightX, contentY, rightW, contentH);

    // Comments box
    pdf.rect(outerX, commentsY, outerW, commentsH);

    const colW = outerW / 3;
    const rowH = commentsH / 3;

    pdf.setFontSize(9);
    for (let r = 0; r < 1; r++) {
        for (let c = 0; c < 1; c++) {
            const idx = r * 1 + c + 1;
            const tx = outerX + c * colW + 10;
            const ty = commentsY + r * rowH + 18;
            pdf.text(`Comentário: ${project.orderList[0]}`, tx, ty);
        }
    }


    // Grid atrás do SVG
    //drawGrid(pdf, leftX, contentY, leftW, contentH, 6);

    // ===== SVG VETORIAL (editável no Corel) =====
    // Fabric normaliza (corrige viewBox deslocado e remove “vazio”)
    await renderSvgWithPdfSvg(
        pdf as any,
        svgUrl,
        -148,
        -176,
        470,
        470
    );


    // ===== THUMB WEBP (transparente) =====
    const imgPad = 6;
    const boxX = rightX + imgPad;
    const boxY = contentY + imgPad;
    const boxW = rightW - imgPad * 2;
    const boxH = contentH - imgPad * 2;

    // WEBP -> PNG (preserva alpha) + fit contain
    const { dataUrl, w: nW, h: nH } = await webpUrlToPngDataUrl(thumbUrl);
    const fitted = fitContain(boxW, boxH, nW, nH);

    pdf.addImage(
        dataUrl,
        "PNG",
        boxX + fitted.x,
        boxY + fitted.y,
        fitted.w,
        fitted.h,
        undefined,
        "FAST"
    );

    pdf.save("layout.pdf");

    (window as any)['stateLoadinScreen2'](false);
}

// ======================
// HELPERS (SVG)
// ======================

async function imageUrlToPngDataUrl(
    url: string
): Promise<{ dataUrl: string; w: number; h: number }> {
    const res = await fetch(url);
    if (!res.ok) throw new Error("Imagem não encontrada");

    const blob = await res.blob();
    const objUrl = URL.createObjectURL(blob);

    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
        const i = new Image();
        i.crossOrigin = "anonymous";
        i.onload = () => resolve(i);
        i.onerror = reject;
        i.src = objUrl;
    });

    const canvas = document.createElement("canvas");
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;

    const ctx = canvas.getContext("2d")!;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0);

    URL.revokeObjectURL(objUrl);

    return {
        dataUrl: canvas.toDataURL("image/png"),
        w: canvas.width,
        h: canvas.height,
    };
}


async function renderSvgWithPdfSvg(
    pdf: any,
    svgUrl: string,
    x: number,
    y: number,
    w: number,
    h: number
) {
    const svgText = await (await fetch(svgUrl)).text();

    const temp = document.createElement("div");
    temp.style.position = "fixed";
    temp.style.left = "-10000px";
    temp.style.top = "-10000px";
    temp.style.width = "1px";
    temp.style.height = "1px";
    temp.style.overflow = "hidden";
    temp.innerHTML = svgText;
    document.body.appendChild(temp);

    const svgElement = temp.querySelector("svg") as SVGSVGElement;
    if (!svgElement) {
        document.body.removeChild(temp);
        throw new Error("SVG inválido");
    }

    svgElement.removeAttribute("width");
    svgElement.removeAttribute("height");
    svgElement.setAttribute("preserveAspectRatio", "xMidYMid meet");

    // opcional: corrige viewBox “lá embaixo”
    try {
        const bbox = (svgElement as any).getBBox?.();
        if (bbox?.width && bbox?.height) {
            svgElement.setAttribute("viewBox", `${bbox.x} ${bbox.y} ${bbox.width} ${bbox.height}`);
        }
    } catch { }

    svgElement.getBoundingClientRect();

    await pdf.svg(svgElement, { x, y, width: w, height: h });

    document.body.removeChild(temp);
}


async function fabricNormalizeSvgFromUrl(url: string): Promise<string> {
    const svgText = await (await fetch(url)).text();

    const { objects, options } = await new Promise<any>((resolve, reject) => {
        fabric.loadSVGFromString(svgText, (objs, opt) => {
            if (!objs || objs.length === 0) reject(new Error("Falha ao carregar SVG no Fabric"));
            else resolve({ objects: objs, options: opt });
        });
    });

    const group = fabric.util.groupSVGElements(objects, options);

    // Bounding real do conteúdo
    const b = group.getBoundingRect(true, true);
    const w = Math.max(1, b.width);
    const h = Math.max(1, b.height);

    // Conteúdo exportado pelo Fabric (paths, etc.)
    const inner = group.toSVG();

    // Move o conteúdo para (0,0) e define viewBox certinho
    return `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${w} ${h}" preserveAspectRatio="xMidYMid meet">
  <g transform="translate(${-b.left} ${-b.top})">
    ${inner}
  </g>
</svg>`.trim();
}

async function svgToPdfContain(
    pdf: jsPDF,
    svgEl: SVGSVGElement,
    boxX: number,
    boxY: number,
    boxW: number,
    boxH: number
) {
    // viewBox do SVG normalizado
    const vb = (svgEl.getAttribute("viewBox") || "0 0 1000 1000")
        .trim()
        .split(/[\s,]+/)
        .map(Number);

    const vbW = vb[2] || 1000;
    const vbH = vb[3] || 1000;

    const scale = Math.min(boxW / vbW, boxH / vbH);

    const drawW = vbW * scale;
    const drawH = vbH * scale;

    const tx = boxX + (boxW - drawW) / 2;
    const ty = boxY + (boxH - drawH) / 2;

    pdf.saveGraphicsState();
    pdf.setCurrentTransformationMatrix(pdf.Matrix(scale, 0, 0, scale, tx, ty));
    await svg2pdf(svgEl, pdf, {} as any);
    pdf.restoreGraphicsState();
}

function textToSvgElement(svgText: string) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(svgText, "image/svg+xml");
    const svg = doc.documentElement as unknown as SVGSVGElement;
    if (!svg.getAttribute("xmlns")) svg.setAttribute("xmlns", "http://www.w3.org/2000/svg");
    return svg;
}

// ======================
// HELPERS (IMAGEM)
// ======================
async function webpUrlToPngDataUrl(
    url: string
): Promise<{ dataUrl: string; w: number; h: number }> {
    const res = await fetch(url);
    if (!res.ok) throw new Error(`Falha ao baixar thumb: ${res.status}`);

    const blob = await res.blob();
    const objUrl = URL.createObjectURL(blob);

    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
        const i = new Image();
        i.crossOrigin = "anonymous";
        i.onload = () => resolve(i);
        i.onerror = reject;
        i.src = objUrl;
    });

    const canvas = document.createElement("canvas");
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;

    const ctx = canvas.getContext("2d")!;
    ctx.clearRect(0, 0, canvas.width, canvas.height); // mantém alpha
    ctx.drawImage(img, 0, 0);

    URL.revokeObjectURL(objUrl);

    return {
        dataUrl: canvas.toDataURL("image/png"),
        w: canvas.width,
        h: canvas.height,
    };
}

function fitContain(boxW: number, boxH: number, imgW: number, imgH: number) {
    const s = Math.min(boxW / imgW, boxH / imgH);
    const w = imgW * s;
    const h = imgH * s;
    return { w, h, x: (boxW - w) / 2, y: (boxH - h) / 2 };
}

// ======================
// HELPERS (GRID)
// ======================
function drawGrid(pdf: jsPDF, x: number, y: number, w: number, h: number, cell: number) {
    pdf.setLineWidth(0.1);

    for (let xx = x; xx <= x + w + 0.001; xx += cell) {
        pdf.line(xx, y, xx, y + h);
    }
    for (let yy = y; yy <= y + h + 0.001; yy += cell) {
        pdf.line(x, yy, x + w, yy);
    }

    pdf.setLineWidth(0.25);
}

// ======================
// TYPES + MODAL (SEU CÓDIGO)
// ======================
export interface Project {
    id: string;
    thumb: string;
    orderNumber: string;
    name: string;
    phone: string;
    email: string;
    city: string;
    orderList: string[];
    sport: string;
    order: [];
}

const mockProjects: Project[] = [];

interface ProjectsModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

function phoneToFolder(phone: string) {
    return (phone || "").replace(/\D/g, "");
}

export function ProjectsModal({ open, onOpenChange }: ProjectsModalProps) {
    const [projects, setProjects] = useState<Project[]>(mockProjects);
    const [searchTerm, setSearchTerm] = useState("");
    const [showAddDialog, setShowAddDialog] = useState(false);
    const [selectedPhoneFolder, setSelectedPhoneFolder] = useState<string | null>(null);

    const handleAddProject = (newProject: Omit<Project, "id">) => {
        const project: Project = { ...newProject, id: Date.now().toString() };
        setProjects([project, ...projects]);
        setShowAddDialog(false);
    };

    (window as any).handleAddClientProject = async (data: any) => {
        const project: Project = { ...data };
        setProjects((prev) => [project, ...prev]);
        setShowAddDialog(false);
    };

    const handleDelete = (id: string) => {
        setProjects((prev) => prev.filter((p) => p.id !== id));
        deleteProjectIframe(id);
    };

    const handleView = (project: Project) => {
        window.open(
            "https://" + window.location.host + "/?g=" + project.sport + "&t=" + project.thumb,
            "_self"
        );
    };

    const handleEdit = (project: Project) => {
    };

    const handleNotify = (project: Project) => {
        const phoneNumber = project.phone;
        const message = "Olá! Analisamos seu projeto e ";
        const url = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;
        window.open(url, "_blank");
    };

    const normalizedSearch = searchTerm.toLowerCase();

    const baseFiltered = useMemo(() => {
        return projects.filter((project) => {
            return (
                project.name.toLowerCase().includes(normalizedSearch) ||
                project.orderNumber.toLowerCase().includes(normalizedSearch) ||
                project.city.toLowerCase().includes(normalizedSearch) ||
                project.email.toLowerCase().includes(normalizedSearch) ||
                project.phone.toLowerCase().includes(normalizedSearch)
            );
        });
    }, [projects, normalizedSearch]);

    const filteredProjects = useMemo(() => {
        if (!selectedPhoneFolder) return baseFiltered;
        return baseFiltered.filter((p) => phoneToFolder(p.phone) === selectedPhoneFolder);
    }, [baseFiltered, selectedPhoneFolder]);

    const folders = useMemo(() => {
        if (selectedPhoneFolder) return [];

        const grouped = baseFiltered.reduce<Record<string, Project[]>>((acc, p) => {
            const key = phoneToFolder(p.phone);
            if (!key) return acc;
            if (!acc[key]) acc[key] = [];
            acc[key].push(p);
            return acc;
        }, {});

        return Object.entries(grouped).map(([phoneFolder, list]) => ({
            phoneFolder,
            count: list.length,
            phone: list[0]?.phone ?? phoneFolder,
            name: list[0]?.name ?? "Sem nome",
        }));
    }, [baseFiltered, selectedPhoneFolder]);

    return (
        <>
            <Dialog
                open={open}
                onOpenChange={(isOpen) => {
                    onOpenChange(isOpen);
                    if (!isOpen) setSelectedPhoneFolder(null);
                }}
            >
                <DialogContent className="max-w-7xl max-h-[90vh] overflow-hidden flex flex-col">
                    <DialogHeader>
                        <DialogTitle className="text-2xl">
                            {selectedPhoneFolder ? "Projetos do Telefone" : "Pastas por Telefone"}
                        </DialogTitle>
                        <DialogDescription>
                            {selectedPhoneFolder
                                ? `Mostrando projetos do telefone: ${selectedPhoneFolder}`
                                : "Clique em um telefone para abrir a pasta e ver os projetos"}
                        </DialogDescription>
                    </DialogHeader>

                    <div className="flex gap-2 mb-4 flex-col">
                        <div className="relative flex-1">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                            <Input
                                placeholder="Buscar por nome, pedido, cidade, email ou telefone..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="pl-10"
                            />
                        </div>

                        {selectedPhoneFolder && (
                            <Button className="bg-[orange]" variant="outline" onClick={() => setSelectedPhoneFolder(null)}>
                                Voltar
                            </Button>
                        )}
                    </div>

                    <div className="flex-1 overflow-y-auto pr-2">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                            {!selectedPhoneFolder ? (
                                folders.map((f) => (
                                    <button
                                        key={f.phoneFolder}
                                        onClick={() => setSelectedPhoneFolder(f.phoneFolder)}
                                        className="text-left"
                                    >
                                        <div className="rounded-lg border bg-card p-4 hover:shadow-lg transition-shadow">
                                            <div className="text-sm text-muted-foreground">Cliente</div>
                                            <div className="text-lg font-semibold text-foreground">{f.name}</div>
                                            <div className="text-sm text-muted-foreground break-all">{f.phone}</div>
                                            <div className="mt-2 text-sm text-muted-foreground">{f.count} projeto(s)</div>
                                        </div>
                                    </button>
                                ))
                            ) : (
                                filteredProjects.map((project) => (
                                    <ProjectCard
                                        key={project.id}
                                        project={project}
                                        onView={handleView}
                                        onEdit={handleEdit}
                                        onDelete={handleDelete}
                                        onNotify={handleNotify}
                                        onDownload={handleDownload}
                                    />
                                ))
                            )}
                        </div>

                        {!selectedPhoneFolder && folders.length === 0 && (
                            <div className="text-center py-12 text-muted-foreground">Nenhum telefone encontrado</div>
                        )}

                        {selectedPhoneFolder && filteredProjects.length === 0 && (
                            <div className="text-center py-12 text-muted-foreground">Nenhum projeto nessa pasta</div>
                        )}
                    </div>
                </DialogContent>
            </Dialog>

            <AddProjectDialog open={showAddDialog} onOpenChange={setShowAddDialog} onAdd={handleAddProject} />
        </>
    );
}
